import { Plant, PlantState } from '../plants/Plant';
import { Game } from '../core/Game';
import { Environment } from '../environment/Environment';
import { eventBus } from '../core/EventBus';

interface SliderControl {
  container: HTMLElement;
  input: HTMLInputElement;
  valueSpan: HTMLElement;
}

interface StageButton {
  button: HTMLButtonElement;
  index: number;
}

interface StateButton {
  button: HTMLButtonElement;
  state: PlantState;
}

const STAGES = [
  { index: 0, id: 'seed', name: '种子' },
  { index: 1, id: 'sprout', name: '萌芽' },
  { index: 2, id: 'seedling', name: '幼苗' },
  { index: 3, id: 'budding', name: '抽穗' },
  { index: 4, id: 'blooming', name: '盛花' },
];

const STATE_LIST: { id: PlantState; name: string }[] = [
  { id: 'thriving', name: '蓬勃' },
  { id: 'lazy', name: '慵懒' },
  { id: 'withering', name: '枯萎' },
  { id: 'dormant', name: '休眠' },
];

const BTN_STYLE = `
  padding: 4px 8px;
  border: 1px solid rgba(200,180,160,0.4);
  border-radius: 6px;
  background: rgba(255,250,245,0.08);
  color: #c8b8a8;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s;
  font-family: inherit;
`;

export class DebugPanel {
  private plant: Plant;
  private game: Game;
  private environment: Environment;
  private panel!: HTMLElement;
  private isOpen: boolean = false;

  private stageButtons: StageButton[] = [];
  private stateButtons: StateButton[] = [];

  private progressSlider!: SliderControl;
  private droopSlider!: SliderControl;
  private saturationSlider!: SliderControl;
  private swaySlider!: SliderControl;
  private browningSlider!: SliderControl;
  private alphaSlider!: SliderControl;
  private lightSlider!: SliderControl;
  private moistureSlider!: SliderControl;
  private tempSlider!: SliderControl;
  private speedSlider!: SliderControl;

  private stateOverrideCheckbox!: HTMLInputElement;
  private lightCycleCheckbox!: HTMLInputElement;
  private gameTimeDisplay!: HTMLElement;

  constructor(plant: Plant | null, game: Game, environment: Environment) {
    this.plant = plant!;
    this.game = game;
    this.environment = environment;
    if (!plant) return;
    this.panel = this.createPanel();
    document.body.appendChild(this.panel);
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    eventBus.on('debug:toggle', this.toggle.bind(this));
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.code === 'Backquote' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      this.toggle();
    }
  }

  toggle(): void {
    if (!this.panel) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.refreshValues();
      this.panel.style.display = 'flex';
    } else {
      this.panel.style.display = 'none';
    }
  }

  private refreshValues(): void {
    const vs = this.plant.visualState;
    const env = this.environment;
    const stage = this.plant.getCurrentStage();

    this.progressSlider.input.value = String(this.plant.stageProgress);
    this.progressSlider.valueSpan.textContent = this.plant.stageProgress.toFixed(2);

    this.droopSlider.input.value = String(vs.droopAngle);
    this.droopSlider.valueSpan.textContent = vs.droopAngle.toFixed(0) + '°';

    this.saturationSlider.input.value = String(vs.colorSaturation);
    this.saturationSlider.valueSpan.textContent = vs.colorSaturation.toFixed(2);

    this.swaySlider.input.value = String(vs.swayAmplitude);
    this.swaySlider.valueSpan.textContent = vs.swayAmplitude.toFixed(1);

    this.browningSlider.input.value = String(vs.browning);
    this.browningSlider.valueSpan.textContent = vs.browning.toFixed(2);

    this.alphaSlider.input.value = String(vs.alpha);
    this.alphaSlider.valueSpan.textContent = vs.alpha.toFixed(2);

    this.lightSlider.input.value = String(this.plant.supplementalLight);
    this.lightSlider.valueSpan.textContent = String(this.plant.supplementalLight);

    this.moistureSlider.input.value = String(this.plant.moisture);
    this.moistureSlider.valueSpan.textContent = String(this.plant.moisture);

    this.tempSlider.input.value = String(Math.round(env.temperature));
    this.tempSlider.valueSpan.textContent = Math.round(env.temperature) + '°C';

    this.speedSlider.input.value = String(this.game.settings.timeSpeed);
    this.speedSlider.valueSpan.textContent = this.game.settings.timeSpeed + 'x';

    this.stateOverrideCheckbox.checked = this.plant.debugStateOverride;
    this.lightCycleCheckbox.checked = this.environment.isLightCycleActive();

    this.gameTimeDisplay.textContent = this.game.getFormattedTime();

    this.updateStageHighlight(stage?.id || 'seed');
    this.updateStateHighlight(this.plant.state);
  }

  private updateStageHighlight(activeId: string): void {
    this.stageButtons.forEach(sb => {
      const active = STAGES[sb.index].id === activeId;
      sb.button.style.background = active
        ? 'rgba(180,160,140,0.3)'
        : 'rgba(255,250,245,0.08)';
      sb.button.style.borderColor = active
        ? 'rgba(200,180,160,0.7)'
        : 'rgba(200,180,160,0.4)';
      sb.button.style.color = active ? '#f0e8d8' : '#c8b8a8';
    });
  }

  private updateStateHighlight(activeState: PlantState): void {
    this.stateButtons.forEach(sb => {
      const active = sb.state === activeState;
      sb.button.style.background = active
        ? 'rgba(180,160,140,0.3)'
        : 'rgba(255,250,245,0.08)';
      sb.button.style.borderColor = active
        ? 'rgba(200,180,160,0.7)'
        : 'rgba(200,180,160,0.4)';
      sb.button.style.color = active ? '#f0e8d8' : '#c8b8a8';
    });
  }

  // ---- DOM Creation ----

  private createPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.id = 'debug-panel';
    panel.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 260px;
      max-height: calc(100vh - 40px);
      overflow-y: auto;
      background: rgba(32, 26, 20, 0.94);
      border-radius: 14px;
      padding: 18px;
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.25),
        0 8px 28px rgba(0, 0, 0, 0.35);
      z-index: 1000;
      font-size: 12px;
      color: #c8b8a8;
      backdrop-filter: blur(12px);
      display: none;
      flex-direction: column;
      gap: 14px;
    `;

    panel.appendChild(this.createHeader());
    panel.appendChild(this.createSection('阶段', this.createStageControls()));
    panel.appendChild(this.createSection('状态', this.createStateControls()));
    panel.appendChild(this.createSection('视觉参数', this.createVisualControls()));
    panel.appendChild(this.createSection('环境', this.createEnvControls()));
    panel.appendChild(this.createSection('时间', this.createTimeControls()));

    return panel;
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;

    const title = document.createElement('span');
    title.textContent = '调试面板';
    title.style.cssText = `
      font-weight: bold;
      font-size: 14px;
      color: #f0e8d8;
    `;

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      color: #8a7a6a;
      font-size: 18px;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;
    `;
    closeBtn.addEventListener('click', () => this.toggle());

    header.appendChild(title);
    header.appendChild(closeBtn);
    return header;
  }

  private createSection(title: string, content: HTMLElement): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = `
      border-top: 1px solid rgba(200,180,160,0.15);
      padding-top: 10px;
    `;

    const heading = document.createElement('h4');
    heading.textContent = title;
    heading.style.cssText = `
      margin: 0 0 8px;
      font-size: 11px;
      font-weight: normal;
      color: #8a7a6a;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    `;

    section.appendChild(heading);
    section.appendChild(content);
    return section;
  }

  // ---- Stage Controls ----

  private createStageControls(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display: flex; gap: 4px; flex-wrap: wrap;';

    STAGES.forEach((s) => {
      const btn = document.createElement('button');
      btn.textContent = s.name;
      btn.style.cssText = BTN_STYLE;
      btn.addEventListener('click', () => {
        this.plant.setStage(s.index);
        this.updateStageHighlight(s.id);
        this.progressSlider.input.value = '0';
        this.progressSlider.valueSpan.textContent = '0.00';
        this.refreshVisualSliders();
      });
      btnRow.appendChild(btn);
      this.stageButtons.push({ button: btn, index: s.index });
    });

    container.appendChild(btnRow);

    this.progressSlider = this.createSlider(
      '进度', 'debug-progress', 0, 1, 0.01,
      this.plant.stageProgress,
      (v) => {
        this.plant.setStageProgress(v);
        this.progressSlider.valueSpan.textContent = v.toFixed(2);
      }
    );
    container.appendChild(this.progressSlider.container);

    return container;
  }

  // ---- State Controls ----

  private createStateControls(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

    const overrideLabel = document.createElement('label');
    overrideLabel.style.cssText = 'display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 11px;';
    this.stateOverrideCheckbox = document.createElement('input');
    this.stateOverrideCheckbox.type = 'checkbox';
    this.stateOverrideCheckbox.checked = false;
    this.stateOverrideCheckbox.addEventListener('change', () => {
      this.plant.debugStateOverride = this.stateOverrideCheckbox.checked;
    });
    overrideLabel.appendChild(this.stateOverrideCheckbox);
    overrideLabel.appendChild(document.createTextNode('覆盖状态'));
    container.appendChild(overrideLabel);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display: flex; gap: 4px; flex-wrap: wrap;';

    STATE_LIST.forEach((s) => {
      const btn = document.createElement('button');
      btn.textContent = s.name;
      btn.style.cssText = BTN_STYLE;
      btn.addEventListener('click', () => {
        this.plant.debugStateOverride = true;
        this.stateOverrideCheckbox.checked = true;
        this.plant.setStateDirectly(s.id);
        this.updateStateHighlight(s.id);
        this.refreshVisualSliders();
      });
      btnRow.appendChild(btn);
      this.stateButtons.push({ button: btn, state: s.id });
    });

    container.appendChild(btnRow);
    return container;
  }

  // ---- Visual Controls ----

  private createVisualControls(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

    const vs = this.plant.visualState;

    this.droopSlider = this.createSlider(
      '倾斜角度', 'debug-droop', 0, 60, 1, vs.droopAngle,
      (v) => {
        this.plant.overrideVisualState({ droopAngle: v });
        this.droopSlider.valueSpan.textContent = v.toFixed(0) + '°';
      },
      '°'
    );
    container.appendChild(this.droopSlider.container);

    this.saturationSlider = this.createSlider(
      '色彩饱和', 'debug-saturation', 0, 1, 0.01, vs.colorSaturation,
      (v) => {
        this.plant.overrideVisualState({ colorSaturation: v });
        this.saturationSlider.valueSpan.textContent = v.toFixed(2);
      }
    );
    container.appendChild(this.saturationSlider.container);

    this.swaySlider = this.createSlider(
      '摆动幅度', 'debug-sway', 0, 8, 0.1, vs.swayAmplitude,
      (v) => {
        this.plant.overrideVisualState({ swayAmplitude: v });
        this.swaySlider.valueSpan.textContent = v.toFixed(1);
      }
    );
    container.appendChild(this.swaySlider.container);

    this.browningSlider = this.createSlider(
      '枯萎程度', 'debug-browning', 0, 1, 0.01, vs.browning,
      (v) => {
        this.plant.overrideVisualState({ browning: v });
        this.browningSlider.valueSpan.textContent = v.toFixed(2);
      }
    );
    container.appendChild(this.browningSlider.container);

    this.alphaSlider = this.createSlider(
      '透明度', 'debug-alpha', 0, 1, 0.01, vs.alpha,
      (v) => {
        this.plant.overrideVisualState({ alpha: v });
        this.alphaSlider.valueSpan.textContent = v.toFixed(2);
      }
    );
    container.appendChild(this.alphaSlider.container);

    const resetBtn = document.createElement('button');
    resetBtn.textContent = '重置视觉';
    resetBtn.style.cssText = `${BTN_STYLE} margin-top: 4px; border-color: rgba(200,160,140,0.5);`;
    resetBtn.addEventListener('click', () => {
      this.plant.resetVisualState();
      this.refreshVisualSliders();
    });
    container.appendChild(resetBtn);

    return container;
  }

  // ---- Environment Controls ----

  private createEnvControls(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

    const env = this.environment;

    this.lightSlider = this.createSlider(
      '光照', 'debug-light', 0, 100, 1, this.plant.supplementalLight,
      (v) => {
        this.plant.supplementalLight = v;
        this.lightSlider.valueSpan.textContent = String(v);
        this.lightCycleCheckbox.checked = this.environment.isLightCycleActive();
      }
    );
    container.appendChild(this.lightSlider.container);

    this.moistureSlider = this.createSlider(
      '湿度', 'debug-moisture', 0, 100, 1, this.plant.moisture,
      (v) => {
        this.plant.moisture = v;
        this.moistureSlider.valueSpan.textContent = String(v);
      }
    );
    container.appendChild(this.moistureSlider.container);

    this.tempSlider = this.createSlider(
      '温度', 'debug-temp', 0, 50, 1, Math.round(env.temperature),
      (v) => {
        this.environment.temperature = v;
        this.tempSlider.valueSpan.textContent = String(v) + '°C';
      }
    );
    container.appendChild(this.tempSlider.container);

    const cycleLabel = document.createElement('label');
    cycleLabel.style.cssText = 'display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 11px; margin-top: 4px;';
    this.lightCycleCheckbox = document.createElement('input');
    this.lightCycleCheckbox.type = 'checkbox';
    this.lightCycleCheckbox.checked = true;
    this.lightCycleCheckbox.addEventListener('change', () => {
      this.environment.setLightCycle(this.lightCycleCheckbox.checked);
    });
    cycleLabel.appendChild(this.lightCycleCheckbox);
    cycleLabel.appendChild(document.createTextNode('自然光周期'));
    container.appendChild(cycleLabel);

    return container;
  }

  // ---- Time Controls ----

  private createTimeControls(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

    this.speedSlider = this.createSlider(
      '速度', 'debug-speed', 1, 20, 1, this.game.settings.timeSpeed,
      (v) => {
        this.game.settings.timeSpeed = v;
        this.speedSlider.valueSpan.textContent = v + 'x';
      },
      'x'
    );
    container.appendChild(this.speedSlider.container);

    this.gameTimeDisplay = document.createElement('div');
    this.gameTimeDisplay.style.cssText = 'font-size: 11px; color: #8a7a6a; margin-top: 2px;';
    this.gameTimeDisplay.textContent = '00:00';
    container.appendChild(this.gameTimeDisplay);

    eventBus.on('game:update', () => {
      if (this.isOpen) {
        this.gameTimeDisplay.textContent = this.game.getFormattedTime();
      }
    });

    return container;
  }

  // ---- Slider Helper ----

  private createSlider(
    label: string,
    _id: string,
    min: number,
    max: number,
    step: number,
    initialValue: number,
    onChange: (value: number) => void,
    _unit?: string
  ): SliderControl {
    const container = document.createElement('div');
    container.style.cssText = 'display: flex; flex-direction: column; gap: 2px;';

    const labelRow = document.createElement('div');
    labelRow.style.cssText = 'display: flex; justify-content: space-between; font-size: 11px;';

    const labelSpan = document.createElement('span');
    labelSpan.textContent = label;
    labelSpan.style.color = '#8a7a6a';

    const valueSpan = document.createElement('span');
    valueSpan.textContent = String(initialValue);

    labelRow.appendChild(labelSpan);
    labelRow.appendChild(valueSpan);

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(initialValue);
    input.style.cssText = `
      width: 100%;
      accent-color: #a09080;
      height: 4px;
      cursor: pointer;
    `;
    input.addEventListener('input', () => {
      onChange(parseFloat(input.value));
    });

    container.appendChild(labelRow);
    container.appendChild(input);

    return { container, input, valueSpan };
  }

  private refreshVisualSliders(): void {
    const vs = this.plant.visualState;
    this.droopSlider.input.value = String(vs.droopAngle);
    this.droopSlider.valueSpan.textContent = vs.droopAngle.toFixed(0) + '°';
    this.saturationSlider.input.value = String(vs.colorSaturation);
    this.saturationSlider.valueSpan.textContent = vs.colorSaturation.toFixed(2);
    this.swaySlider.input.value = String(vs.swayAmplitude);
    this.swaySlider.valueSpan.textContent = vs.swayAmplitude.toFixed(1);
    this.browningSlider.input.value = String(vs.browning);
    this.browningSlider.valueSpan.textContent = vs.browning.toFixed(2);
    this.alphaSlider.input.value = String(vs.alpha);
    this.alphaSlider.valueSpan.textContent = vs.alpha.toFixed(2);
  }
}
